/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * UI domain - Performance optimization utilities for throttling and batching
 * performance.client.ts - UI update batching and network request throttling utilities
 */

export interface UIBatchConfig {
  batchDelay: number;
  maxBatchSize: number;
  slowUpdateThreshold: number;
}

export interface NetworkThrottleConfig {
  minInterval: number;
  maxRequestsPerPeriod: number;
  monitoringPeriod: number;
  indicatorTimeout: number;
}

const DEFAULT_UI_BATCH_CONFIG: UIBatchConfig = {
  batchDelay: 16,
  maxBatchSize: 100,
  slowUpdateThreshold: 10,
};

const DEFAULT_NETWORK_THROTTLE_CONFIG: NetworkThrottleConfig = {
  minInterval: 100,
  maxRequestsPerPeriod: 10,
  monitoringPeriod: 5000,
  indicatorTimeout: 10000,
};

let isUIUpdateInProgress = false;
let pendingUIUpdates: (() => void)[] = [];
let uiUpdateTimeoutId: number | null = null;
let uiBatchConfig = DEFAULT_UI_BATCH_CONFIG;

let lastNetworkRequestTime = 0;
let networkRequestTimestamps: number[] = [];
let networkPerformanceIssues = false;
let networkThrottleConfig = DEFAULT_NETWORK_THROTTLE_CONFIG;

export function configureUIBatching(config: Partial<UIBatchConfig>): void {
  uiBatchConfig = { ...DEFAULT_UI_BATCH_CONFIG, ...config };
}

export function configureNetworkThrottling(
  config: Partial<NetworkThrottleConfig>,
): void {
  networkThrottleConfig = { ...DEFAULT_NETWORK_THROTTLE_CONFIG, ...config };
}

export function batchUIUpdate(updateFn: () => void): void {
  pendingUIUpdates.push(updateFn);

  if (pendingUIUpdates.length > uiBatchConfig.maxBatchSize) {
    console.warn(
      `[PERF] UI batch size exceeded ${uiBatchConfig.maxBatchSize}, processing immediately`,
    );
    processBatchedUpdates();
    return;
  }

  if (uiUpdateTimeoutId !== null) {
    clearTimeout(uiUpdateTimeoutId);
  }

  uiUpdateTimeoutId = window.setTimeout(() => {
    if (isUIUpdateInProgress) {
      uiUpdateTimeoutId = window.setTimeout(
        processBatchedUpdates,
        uiBatchConfig.batchDelay,
      );
      return;
    }

    processBatchedUpdates();
  }, uiBatchConfig.batchDelay);
}

export function processBatchedUpdates(): void {
  if (isUIUpdateInProgress || pendingUIUpdates.length === 0) {
    return;
  }

  isUIUpdateInProgress = true;
  const startTime = performance.now();

  try {
    const updates = [...pendingUIUpdates];
    pendingUIUpdates = [];

    updates.forEach((updateFn, index) => {
      try {
        updateFn();
      } catch (error) {
        console.error(`[PERF] Error processing UI update ${index}:`, error);
      }
    });

    const updateTime = performance.now() - startTime;
    if (updateTime > uiBatchConfig.slowUpdateThreshold) {
      console.warn(
        `[PERF] Slow UI update took ${updateTime.toFixed(2)}ms for ${updates.length} updates`,
      );
    }
  } catch (error) {
    console.error("[PERF] Critical error in processBatchedUpdates:", error);
  } finally {
    isUIUpdateInProgress = false;
    uiUpdateTimeoutId = null;

    if (pendingUIUpdates.length > 0) {
      batchUIUpdate(pendingUIUpdates.shift()!);
    }
  }
}

export function throttleNetworkRequest<T extends (...args: any[]) => any>(
  fn: T,
  minInterval?: number,
): (...args: Parameters<T>) => boolean {
  const interval = minInterval ?? networkThrottleConfig.minInterval;

  return (...args: Parameters<T>): boolean => {
    const now = Date.now();

    if (now - lastNetworkRequestTime < interval) {
      console.warn(
        `[PERF] Network request throttled (${now - lastNetworkRequestTime}ms since last, min: ${interval}ms)`,
      );
      return false;
    }

    lastNetworkRequestTime = now;
    networkRequestTimestamps.push(now);

    networkRequestTimestamps = networkRequestTimestamps.filter(
      (t) => now - t < networkThrottleConfig.monitoringPeriod,
    );

    try {
      const result = fn(...args);

      if (
        networkRequestTimestamps.length >
        networkThrottleConfig.maxRequestsPerPeriod
      ) {
        if (!networkPerformanceIssues) {
          networkPerformanceIssues = true;
          console.warn(
            "[PERF] Network performance issues detected - too many requests",
          );
          showNetworkPerformanceIndicator();
        }
      }

      return Boolean(result);
    } catch (error) {
      console.error("[PERF] Error in throttled network request:", error);
      return false;
    }
  };
}

export function showNetworkPerformanceIndicator(
  targetElement?: HTMLElement,
): void {
  const lobbyListEl = targetElement || document.getElementById("lobbyList");

  if (lobbyListEl && !lobbyListEl.querySelector(".network-slow-indicator")) {
    const indicator = document.createElement("div");
    indicator.className = "network-slow-indicator";
    indicator.innerHTML = `
      <span class="indicator-icon">⚠</span>
      <span class="indicator-text">Network slow - lobby updates may be delayed</span>
    `;

    lobbyListEl.insertBefore(indicator, lobbyListEl.firstChild);

    setTimeout(() => {
      indicator.remove();
      networkPerformanceIssues = false;
    }, networkThrottleConfig.indicatorTimeout);
  } else if (!lobbyListEl) {
    console.warn(
      "[PERF] Network performance issues detected but no UI element available for indicator",
    );
  }
}

export function getUIBatchingStats(): {
  pendingUpdates: number;
  isInProgress: boolean;
  config: UIBatchConfig;
} {
  return {
    pendingUpdates: pendingUIUpdates.length,
    isInProgress: isUIUpdateInProgress,
    config: { ...uiBatchConfig },
  };
}

export function getNetworkThrottlingStats(): {
  lastRequestTime: number;
  requestCount: number;
  hasPerformanceIssues: boolean;
  timeSinceLastRequest: number;
  config: NetworkThrottleConfig;
} {
  return {
    lastRequestTime: lastNetworkRequestTime,
    requestCount: networkRequestTimestamps.length,
    hasPerformanceIssues: networkPerformanceIssues,
    timeSinceLastRequest: Date.now() - lastNetworkRequestTime,
    config: { ...networkThrottleConfig },
  };
}

export function resetNetworkThrottling(): void {
  lastNetworkRequestTime = 0;
  networkRequestTimestamps = [];
  networkPerformanceIssues = false;
}

export function flushPendingUIUpdates(): void {
  if (pendingUIUpdates.length > 0) {
    processBatchedUpdates();
  }
}

export function clearPendingUIUpdates(): void {
  pendingUIUpdates = [];
  if (uiUpdateTimeoutId !== null) {
    clearTimeout(uiUpdateTimeoutId);
    uiUpdateTimeoutId = null;
  }
}
